import React from "react";
import { Select, DatePicker, Input, Button } from "antd";
import { archiveToggleLabel } from "helpers/postArchive";
import "./ContentFilterBar.scss";

const { RangePicker } = DatePicker;

// Generic filter bar shared by /all-posts and /all-ideas. The parent owns
// the filter state and the filtered list so the page can keep its existing
// Redux/Kanban wiring; this component just renders the controls and
// reports changes via setFilters.
//
// filters: { brandId, status: string[], dateRange: [moment, moment] | null, keyword }
// brands:  array of { _id, title }
// statusOptions: [{ value, label }]
export default function ContentFilterBar({
  filters,
  setFilters,
  brands = [],
  statusOptions = [],
  resultCount,
  totalCount,
  // Optional Archive-shelf toggle, integrated INTO the filter bar (sits
  // right before the "X of Y" count). When `onToggleArchived` is supplied
  // the control renders; pages that don't have an archive shelf simply omit
  // it and the bar looks exactly as it did before.
  showArchived,
  onToggleArchived,
  archivedCount = 0
}) {
  const onClear = () =>
    setFilters({ brandId: undefined, status: [], dateRange: null, keyword: "" });

  const set = (patch) => setFilters({ ...filters, ...patch });

  return (
    <div className="content-filter-bar">
      <Input.Search
        allowClear
        placeholder="Search title or text…"
        value={filters.keyword || ""}
        onChange={(e) => set({ keyword: e.target.value })}
        style={{ width: 240 }}
      />

      <Select
        allowClear
        showSearch
        placeholder="Brand"
        value={filters.brandId}
        onChange={(v) => set({ brandId: v })}
        optionFilterProp="label"
        style={{ width: 200 }}
        options={brands.map((b) => ({ value: b._id, label: b.title || b.name || b._id }))}
      />

      <Select
        mode="multiple"
        allowClear
        placeholder="Status"
        value={filters.status || []}
        onChange={(v) => set({ status: v })}
        style={{ minWidth: 220 }}
        options={statusOptions}
        maxTagCount="responsive"
      />

      <RangePicker
        value={filters.dateRange || null}
        onChange={(v) => set({ dateRange: v })}
        allowClear
      />

      {(filters.brandId || filters.status?.length || filters.dateRange || filters.keyword) && (
        <Button onClick={onClear} type="link">Clear</Button>
      )}

      {onToggleArchived && (
        <Button
          type={showArchived ? "primary" : "default"}
          className="cfb-archive-toggle"
          onClick={onToggleArchived}
          title={showArchived ? "Hide the archive shelf" : "Show archived posts"}
        >
          {archiveToggleLabel(showArchived, archivedCount)}
        </Button>
      )}

      <span className="cfb-count">
        {resultCount != null && totalCount != null
          ? `${resultCount} of ${totalCount}`
          : resultCount != null
            ? `${resultCount}`
            : ""}
      </span>
    </div>
  );
}

// Pure helper: apply the filter state to a list of items. Date matching uses
// item.date / item.scheduled_at / item.createdAt (whichever is set first).
export function applyContentFilters(items = [], filters = {}) {
  const { brandId, status = [], dateRange, keyword } = filters;
  const kw = (keyword || "").trim().toLowerCase();
  const [from, to] = dateRange || [];
  const fromMs = from ? from.startOf("day").valueOf() : null;
  const toMs = to ? to.endOf("day").valueOf() : null;

  return items.filter((it) => {
    if (brandId) {
      const itBrand = String(it.brandId?._id || it.brandId || it.brand_id || "");
      if (itBrand !== String(brandId)) return false;
    }
    if (status.length) {
      const s1 = String(it.queueStatus || "");
      const s2 = String(it.status || "");
      if (!status.includes(s1) && !status.includes(s2)) return false;
    }
    if (fromMs || toMs) {
      const d = it.date || it.scheduled_at || it.createdAt;
      const ts = d ? new Date(d).getTime() : null;
      if (ts == null) return false;
      if (fromMs && ts < fromMs) return false;
      if (toMs && ts > toMs) return false;
    }
    if (kw) {
      const hay = `${it.title || ""} ${it.textContent || ""} ${it.description || ""} ${it.prompt || ""}`.toLowerCase();
      if (!hay.includes(kw)) return false;
    }
    return true;
  });
}
